import { getLogger } from './logging/structured-logger';
import type { LogEntry } from './logging/types';

interface _ExtendedGlobal {
  requestId?: string;
  userId?: string;
  sessionId?: string;
  organizationId?: string;
  workspaceId?: string;
}

/**
 * 標準形式 `logger.error(message, { error, ...context })` の `error` を例外として取り出す。
 * 見るのは最上位の `error` キーだけで、ネストの自動探索や文字列の昇格はしない。
 */
function extractErrorField(value: unknown): { error: Error; rest: Record<string, unknown> } | undefined {
  if (!value || typeof value !== 'object' || !((value as { error?: unknown }).error instanceof Error)) {
    return undefined;
  }
  const { error, ...rest } = value as { error: Error } & Record<string, unknown>;
  return { error, rest };
}

/** 引数を、最初の Error・プリミティブ（メッセージ）・それ以外（metadata）に振り分ける。 */
function partitionArgs(args: unknown[]): { primitiveParts: string[]; metadataParts: unknown[]; directError?: Error } {
  const primitiveParts: string[] = [];
  const metadataParts: unknown[] = [];
  let directError: Error | undefined;

  for (const arg of args) {
    if (arg instanceof Error && !directError) {
      directError = arg;
      continue;
    }
    const type = typeof arg;
    if (type === 'string' || type === 'number' || type === 'boolean') {
      primitiveParts.push(String(arg));
    } else if (arg !== undefined) {
      metadataParts.push(arg);
    }
  }
  return { primitiveParts, metadataParts, directError };
}

// 互換性のためのレガシーロガーラッパー
class LegacyLogger {
  private structuredLogger = getLogger();
  private logLevel: string;

  constructor() {
    // クライアントでも制御できるように NEXT_PUBLIC_LOG_LEVEL を優先
    const level = (process.env.NEXT_PUBLIC_LOG_LEVEL || process.env.LOG_LEVEL || 'info').toLowerCase();
    this.logLevel = level;
  }

  private normalizeArgs(args: unknown[]): { message: string; error?: Error; context?: Partial<LogEntry> } {
    if (args.length === 0) {
      return { message: '' };
    }
    const { primitiveParts, metadataParts, directError } = partitionArgs(args);
    let pickedError = directError;
    const extracted = pickedError || metadataParts.length !== 1 ? undefined : extractErrorField(metadataParts[0]);
    if (extracted) {
      pickedError = extracted.error;
      metadataParts[0] = extracted.rest;
    }

    const message = primitiveParts.join(' ');
    const context: Partial<LogEntry> | undefined = metadataParts.length > 0
      ? { metadata: metadataParts.length === 1 ? (metadataParts[0] as Record<string, unknown>) : { args: metadataParts } }
      : undefined;

    return { message, error: pickedError, context };
  }

  debug(...args: unknown[]) {
    if (this.logLevel !== 'debug') return;
    const { message, context } = this.normalizeArgs(args);
    this.structuredLogger.debug(message, context);
  }

  info(...args: unknown[]) {
    if (!(this.logLevel === 'debug' || this.logLevel === 'info')) return;
    const { message, context } = this.normalizeArgs(args);
    this.structuredLogger.info(message, context);
  }

  warn(...args: unknown[]) {
    if (!(this.logLevel === 'debug' || this.logLevel === 'info' || this.logLevel === 'warn')) return;
    const { message, error, context } = this.normalizeArgs(args);
    this.structuredLogger.warn(message, error, context);
  }

  error(...args: unknown[]) {
    const { message, error, context } = this.normalizeArgs(args);
    this.structuredLogger.error(message, error, context);
  }

  // Reserved for future: enrich context with global correlation IDs if needed
}

export const logger = new LegacyLogger();

// 新しい構造化ロガーもエクスポート
// 段階的に移行できるようにする
export const structuredLogger = getLogger();
export type { LogLevel, Environment, LogEntry } from './logging/types';
